// @flow
import React from 'react';
import {Link} from '@homeaway/react-localization-components';
import Experiment, {On, Off} from '@homeaway/react-experiment';
import {DualPrice} from '../DualPrice/DualPrice';
import {VrboPriceSummary} from '@homeaway/react-price-summary';
import {VRBO_TOTAL_PRICE_POSA_PRICE_SUMMARY} from '../../abacus/experiments';

import CancellationMessage from '../CancellationMessage/CancellationMessage';
import RoomsBedsDetails from './Details/RoomsBedsDetails/RoomsBedsDetails';
import PropertyDetails from './Details/PropertyDetails/PropertyDetails';
import ColorizedRating from '../Rating/ColorizedRating/ColorizedRating';
import {FREE_CANCELLATION} from '../../constants/badges';
import {SuperlativeBadge, NewListingBadge, RankedBadges} from './Badges/Badges';
import WebRatingBadge from '../WebRatingBadge/WebRatingBadge';
import type {Props} from './HitInfo';

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    intl,
    listing,
    onClick,
    isModalOpen,
    setModalOpen
}: Props) {
    const {headline} = listing.propertyMetadata || {};
    const rankedBadges = listing.rankedBadges || [];
    const superlativesBadges = listing.superlativesBadges || [];
    const webRatingBadges = listing.webRatingBadges || [];
    const showWebRatingBadge =
        listing.reviewCount === 0 && webRatingBadges && webRatingBadges.length;

    const freeCancellationBadge = rankedBadges.find(
        (e) => e.id === FREE_CANCELLATION
    );

    const DualPriceWrapper = () => {
        const prices = listing.prices;

        return (
            <footer className="media-flex__footer">
                <div className="HitInfo__badgeRatingGroup--old  HitInfo__badgeRatingGroup--left HitInfo__badgeRatingGroup--bottom">
                    <SuperlativeBadge superlativesBadges={superlativesBadges} />
                    <div className="HitInfo__starRating">
                        {showWebRatingBadge ? (
                            <WebRatingBadge
                                badge={webRatingBadges[0]}
                                renderStarIcon
                            />
                        ) : (
                            <ColorizedRating listing={listing} />
                        )}
                        <RankedBadges rankedBadges={rankedBadges} />
                    </div>
                </div>
                <div className="HitInfo__priceSuperlativeGroup--new HitInfo__badgeRatingGroup--right HitInfo__badgeRatingGroup--bottom">
                    <NewListingBadge superlativesBadges={superlativesBadges} />
                    {freeCancellationBadge && (
                        <CancellationMessage
                            freeCancellationBadge={freeCancellationBadge}
                        />
                    )}
                    <Experiment name={VRBO_TOTAL_PRICE_POSA_PRICE_SUMMARY}>
                        <On>
                            <VrboPriceSummary {...prices} mainPrice="total" />
                        </On>
                        <Off>
                            <DualPrice
                                listing={listing}
                                intl={intl}
                                isDesktop
                                withTooltip
                                showInstantBookIcon={false}
                                isModalOpen={isModalOpen}
                                setModalOpen={setModalOpen}
                            />
                        </Off>
                    </Experiment>
                </div>
            </footer>
        );
    };

    return (
        <Link
            target={'_blank'}
            className="media-flex__content"
            href={listing.detailPageUrl}
            onClick={onClick}
            tabIndex="0"
        >
            <div className="media-flex__body HitInfo__content">
                <PropertyDetails listing={listing} />
                <h2 className="HitInfo__headline hover-text" aria-hidden="true">
                    {headline}
                </h2>
                <RoomsBedsDetails listing={listing} />
            </div>
            <DualPriceWrapper />
        </Link>
    );
}
